import React, { useState, useEffect } from "react";
import api, { apiError, inr } from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Plus, Trash2, Save, Package, BedDouble, Sparkles, ShieldCheck } from "lucide-react";
import { PackagePhotos } from "@/components/PackagePhotos";

const slug = (s) => (s || "item").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") + "-" + Math.random().toString(36).slice(2, 6);

export default function PricingEditor({ onSaved }) {
  const { config, refreshPricing } = useWorkspace();
  const [packages, setPackages] = useState(() => (config?.packages || []).map((p) => ({ ...p })));
  const [occupancy, setOccupancy] = useState(() => (config?.occupancy || []).map((o) => ({ ...o })));
  const [addons, setAddons] = useState(() => (config?.addons || []).map((a) => ({ ...a })));
  const [visaFee, setVisaFee] = useState(config?.visa_insurance_fee ?? 0);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!config) return;
    setPackages((cur) => (cur.length ? cur : (config.packages || []).map((p) => ({ ...p }))));
    setOccupancy((cur) => (cur.length ? cur : (config.occupancy || []).map((o) => ({ ...o }))));
    setAddons((cur) => (cur.length ? cur : (config.addons || []).map((a) => ({ ...a }))));
    setVisaFee((cur) => cur || (config.visa_insurance_fee ?? 0));
  }, [config]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/pricing", {
        workspace_id: localStorage.getItem("workspace_id"),
        packages: packages.map((p) => ({ id: p.id || slug(p.name), name: p.name, price: Number(p.price) || 0, mrp: Number(p.mrp) || 0, offer_until: p.offer_until || "", desc: p.desc || "" })),
        occupancy: occupancy.map((o) => ({ id: o.id || slug(o.name), name: o.name, surcharge: Number(o.surcharge) || 0 })),
        addons: addons.map((a) => ({ id: a.id || slug(a.name), name: a.name, price: Number(a.price) || 0 })),
        visa_insurance_fee: Number(visaFee) || 0,
      });
      await refreshPricing();
      toast.success("Pricing updated");
      onSaved?.();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Packages */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <p className="eyebrow text-gold flex items-center gap-2"><Package className="w-4 h-4" /> Umrah Packages</p>
          <Button size="sm" variant="outline" data-testid="add-package-btn" className="border-gold/40 text-gold gap-1.5"
            onClick={() => setPackages([...packages, { id: slug("package"), name: "", price: 0, desc: "" }])}>
            <Plus className="w-3.5 h-3.5" /> Add Package
          </Button>
        </div>
        <div className="space-y-2">
          {packages.map((p, i) => (
            <Card key={i} data-testid={`pkg-editor-row-${i}`} className="p-3 bg-card/40 border-border/60 grid grid-cols-12 gap-2 items-center">
              <div className="col-span-4"><Label className="text-[10px] text-muted-foreground">Name</Label>
                <Input value={p.name} onChange={(e) => { const n = [...packages]; n[i].name = e.target.value; setPackages(n); }} className="mt-0.5 h-8 bg-card/60" placeholder="Package name" /></div>
              <div className="col-span-3"><Label className="text-[10px] text-muted-foreground">Offer price ₹</Label>
                <Input type="number" value={p.price} onChange={(e) => { const n = [...packages]; n[i].price = e.target.value; setPackages(n); }} className="mt-0.5 h-8 bg-card/60" />
                <Label className="text-[10px] text-muted-foreground">Full price ₹ (shown crossed out)</Label>
                <Input type="number" data-testid={`pricing-mrp-${i}`} value={p.mrp || ""} onChange={(e) => { const n = [...packages]; n[i].mrp = e.target.value; setPackages(n); }} className="mt-0.5 h-8 bg-card/60" placeholder="e.g. 95000" />
                <Label className="text-[10px] text-muted-foreground">Offer ends on (optional)</Label>
                <Input type="date" data-testid={`pricing-offer-until-${i}`} value={p.offer_until || ""} onChange={(e) => { const n = [...packages]; n[i].offer_until = e.target.value; setPackages(n); }} className="mt-0.5 h-8 bg-card/60" />
                {Number(p.mrp) > Number(p.price) && Number(p.price) > 0 && <p className="text-[10px] text-red-300 mt-0.5" data-testid={`pricing-offer-preview-${i}`}>Shows: <s>{inr(p.mrp)}</s> {inr(p.price)} · {inr(p.mrp - p.price)} OFF</p>}</div>
              <div className="col-span-4"><Label className="text-[10px] text-muted-foreground">Details</Label>
                <Input value={p.desc} onChange={(e) => { const n = [...packages]; n[i].desc = e.target.value; setPackages(n); }} className="mt-0.5 h-8 bg-card/60" placeholder="Hotel, nights, transfers…" /></div>
              <div className="col-span-1 flex justify-end pt-4">
                <Button size="icon" variant="ghost" data-testid={`del-package-${i}`} className="h-8 w-8 text-destructive" onClick={() => setPackages(packages.filter((_, x) => x !== i))}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
              {p.id && <PackagePhotos pkgId={p.id} />}
            </Card>
          ))}
        </div>
      </section>

      {/* Occupancy */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <p className="eyebrow text-gold flex items-center gap-2"><BedDouble className="w-4 h-4" /> Room Occupancy</p>
          <Button size="sm" variant="outline" data-testid="add-occupancy-btn" className="border-gold/40 text-gold gap-1.5"
            onClick={() => setOccupancy([...occupancy, { id: slug("room"), name: "", surcharge: 0 }])}>
            <Plus className="w-3.5 h-3.5" /> Add Room Type
          </Button>
        </div>
        <div className="space-y-2">
          {occupancy.map((o, i) => (
            <Card key={i} className="p-3 bg-card/40 border-border/60 grid grid-cols-12 gap-2 items-center">
              <div className="col-span-7"><Label className="text-[10px] text-muted-foreground">Room type</Label>
                <Input value={o.name} onChange={(e) => { const n = [...occupancy]; n[i].name = e.target.value; setOccupancy(n); }} className="mt-0.5 h-8 bg-card/60" /></div>
              <div className="col-span-4"><Label className="text-[10px] text-muted-foreground">Surcharge ₹</Label>
                <Input type="number" value={o.surcharge} onChange={(e) => { const n = [...occupancy]; n[i].surcharge = e.target.value; setOccupancy(n); }} className="mt-0.5 h-8 bg-card/60" /></div>
              <div className="col-span-1 flex justify-end pt-4">
                <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => setOccupancy(occupancy.filter((_, x) => x !== i))}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* Add-ons */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <p className="eyebrow text-gold flex items-center gap-2"><Sparkles className="w-4 h-4" /> Premium Add-ons</p>
          <Button size="sm" variant="outline" data-testid="add-addon-btn" className="border-gold/40 text-gold gap-1.5"
            onClick={() => setAddons([...addons, { id: slug("addon"), name: "", price: 0 }])}>
            <Plus className="w-3.5 h-3.5" /> Add Add-on
          </Button>
        </div>
        <div className="space-y-2">
          {addons.map((a, i) => (
            <Card key={i} className="p-3 bg-card/40 border-border/60 grid grid-cols-12 gap-2 items-center">
              <div className="col-span-7"><Label className="text-[10px] text-muted-foreground">Add-on</Label>
                <Input value={a.name} onChange={(e) => { const n = [...addons]; n[i].name = e.target.value; setAddons(n); }} className="mt-0.5 h-8 bg-card/60" /></div>
              <div className="col-span-4"><Label className="text-[10px] text-muted-foreground">Price ₹</Label>
                <Input type="number" value={a.price} onChange={(e) => { const n = [...addons]; n[i].price = e.target.value; setAddons(n); }} className="mt-0.5 h-8 bg-card/60" /></div>
              <div className="col-span-1 flex justify-end pt-4">
                <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => setAddons(addons.filter((_, x) => x !== i))}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* Visa fee */}
      <section>
        <p className="eyebrow text-gold flex items-center gap-2 mb-2"><ShieldCheck className="w-4 h-4" /> Visa + Insurance Fee</p>
        <Card className="p-3 bg-card/40 border-border/60 flex items-center gap-3">
          <Label className="text-sm flex-1">Visa + Travel Insurance charge (₹)</Label>
          <Input type="number" value={visaFee} onChange={(e) => setVisaFee(e.target.value)} className="w-40 h-8 bg-card/60" data-testid="visa-fee-input" />
          <span className="text-gold font-mono text-sm w-24 text-right">{inr(Number(visaFee) || 0)}</span>
        </Card>
      </section>

      <div className="flex justify-end pt-2">
        <Button data-testid="save-pricing-btn" onClick={save} disabled={saving} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-2">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Pricing"}
        </Button>
      </div>
    </div>
  );
}
